import {useHttpsCallable} from 'react-firebase-hooks/functions';
import {getFunctions, HttpsCallableResult} from 'firebase/functions';

/**
 * The signature of an `onCall` Firebase HTTPS callable function as
 * `useHttpsCallable` actually exposes it.
 *
 * **Resolves `undefined` when the call fails.** `react-firebase-hooks` catches
 * the error, records it in the hook's third tuple element, and resolves with
 * nothing — it does not reject. A caller writing
 * `const {data} = await callFn(...)` therefore throws "Cannot destructure
 * property 'data' of 'undefined'" on any backend failure, which says nothing
 * about the actual cause and sends the reader looking in the wrong place.
 *
 * This type previously omitted `| undefined`, which made that unsafe
 * destructure look correct to the compiler. Check the result before using it,
 * or read the hook's `error` element.
 */
export type CipHttpsCallableFunction<TRequest, TResponse> = (
  data: TRequest,
) => Promise<HttpsCallableResult<TResponse> | undefined>;

/**
 * The result of calling `useCipHttpsCallable`. Matches the signature of
 * `useHttpsCallable` from `react-firebase-hooks`. The first element of the
 * tuple is the callable function, the second element is a boolean indicating if
 * the function is loading, and the third element is an error if one occurred or
 * undefined.
 */
export type UseCipHttpsCallableResult<TRequest, TResponse> = readonly [
  CipHttpsCallableFunction<TRequest, TResponse>,
  boolean,
  Error | undefined,
];

/**
 * A hook to be used in place of `useHttpsCallable` that automatically discovers
 * the functions instance.
 *
 * TODO: #971 - Expand usage of this function.
 * @param name The name of the callable function to call.
 * @returns Returns: {@inheritDoc UseCipHttpsCallableResult}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
export function useCipHttpsCallable<TRequest = any, TResponse = any>(
  name: string,
): UseCipHttpsCallableResult<TRequest, TResponse> {
  const functions = getFunctions();
  return useHttpsCallable<TRequest, TResponse>(functions, name);
}

/**
 * Reads the payload out of a callable result, turning a swallowed failure into
 * a real error.
 *
 * `useHttpsCallable` resolves `undefined` rather than rejecting when the call
 * fails, so `const {data} = await callFn(...)` throws "Cannot destructure
 * property 'data' of 'undefined'" — a message that names neither the callable
 * nor the cause. This raises something a reader can act on instead.
 *
 * @param result - What the callable resolved with.
 * @param callableName - Name of the callable, for the error message.
 * @return The response payload.
 * @throws {Error} When the call failed. The underlying cause is in the hook's
 *   `error` tuple element and in the browser console.
 */
export function unwrapCallableResult<TResponse>(
  result: HttpsCallableResult<TResponse> | undefined,
  callableName: string,
): TResponse {
  if (!result) {
    throw new Error(
      `${callableName} did not return a result; the call failed. See the ` +
        `console for the underlying error.`,
    );
  }
  return result.data;
}
